import {
  IFilterValue,
  TFilterPossibleValues,
} from "src/shared/components/Table/model/types";
import { EQueryTagTypes } from "src/shared/types/api";
import { IBaseQueryParams } from "src/shared/types/query";

export function queryCleanEmpty<T extends Record<string, unknown>>(
  query: T,
): Partial<T> {
  return Object.fromEntries(
    Object.entries(query)
      .filter(([, value]) => !!value)
      .map(([k, v]) => [k, String(v)]),
  ) as Partial<T>;
}

export function queryFromSearchParams<T = IBaseQueryParams>(
  params: URLSearchParams,
): Partial<T> {
  const query: Record<string, string | string[]> = {};
  params.forEach((value, key) => {
    query[key] = value.split(",");
    if (query[key].length === 1) {
      query[key] = query[key][0];
    }
  });

  return query as Partial<T>;
}

export const getNewSearchParams = (
  searchParams: URLSearchParams,
  key: string,
  value: string,
) => {
  if (value) {
    searchParams.set(key, value);
  } else searchParams.delete(key);

  return searchParams;
};

export const getNewSearchParamsFromFilter = (
  searchParams: URLSearchParams,
  filter: IFilterValue<TFilterPossibleValues | null>[],
) => {
  filter.forEach(({ name, value }) => {
    if (Array.isArray(value)) {
      if (value.length === 0) {
        searchParams.delete(name);
      } else {
        searchParams.set(name, value.join(","));
      }

      return;
    }

    if (value !== undefined && value !== null && value !== "") {
      searchParams.set(name, String(value));
    } else {
      searchParams.delete(name);
    }
  });

  return searchParams;
};

export const getNewEmptySearchParamsFromFilter = (
  searchParams: URLSearchParams,
  filterNames: Array<string>,
) => {
  filterNames.forEach((name) => {
    searchParams.delete(name);
  });

  return searchParams;
};

export function getProvidesTags<T extends { id: string | number }>(
  content: T[],
  queryTagType: EQueryTagTypes,
) {
  return content.map((item) => ({
    type: queryTagType,
    id: String(item.id),
  }));
}

export function getPagination({
  page,
  pageSize,
  params,
}: {
  page: number;
  pageSize: number;
  params: URLSearchParams;
}) {
  if (String(page) !== params.get("page")) {
    params = getNewSearchParams(params, "page", String(page));
  }
  if (String(pageSize) !== params.get("per_page")) {
    params = getNewSearchParams(params, "per_page", String(pageSize));
  }

  return params;
}

export type ArrayEncode = "brackets" | "repeat" | "csv";

export function sanitizeQueryParams<T extends Record<string, unknown>>(
  obj: T,
): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => {
      if (Array.isArray(value)) return value.length > 0;

      return value !== undefined && value !== null && value !== "";
    }),
  ) as Partial<T>;
}
